import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/firebase';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  FacebookAuthProvider,
  TwitterAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  OAuthProvider,
  sendEmailVerification,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  getAdditionalUserInfo
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  getStorage, 
  ref as storageRef, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // 清除错误
  const clearError = () => {
    setError(null);
  };

  // 检查用户是否为管理员
  const checkAdminStatus = async (user) => {
    if (!user) return false;
    try {
      console.log("检查用户管理员状态...");
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        console.log("用户文档不存在");
        return false;
      }
      const userData = userDoc.data();
      console.log("用户数据:", userData);
      return userData.role === 'admin';
    } catch (err) {
      console.error("检查管理员状态失败:", err);
      return false;
    }
  };

  // 获取用户数据
  const fetchUserData = async (user) => {
    if (!user) return null;
    try {
      console.log("获取用户数据...");
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        console.log("用户文档不存在");
        return null;
      }
      return userDoc.data();
    } catch (err) {
      console.error("获取用户数据失败:", err);
      return null;
    }
  };

  // 监听用户认证状态
  useEffect(() => {
    console.log("设置认证状态监听器...");
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("认证状态变化:", user ? "已登录" : "未登录");
      setCurrentUser(user);
      
      if (user) {
        try {
          console.log("开始获取用户状态...");
          const [adminStatus, userDataResult] = await Promise.all([
            checkAdminStatus(user),
            fetchUserData(user)
          ]);
          
          console.log("用户状态获取完成:", { isAdmin: adminStatus, userData: !!userDataResult });
          // 延迟设置状态，确保React渲染正确
          setTimeout(() => {
            setIsAdmin(adminStatus);
            setUserData(userDataResult);
            setLoading(false);
          }, 100);
        } catch (err) {
          console.error("获取用户状态失败:", err);
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setUserData(null);
        setLoading(false);
      }
    });
  
    return () => unsubscribe();
  }, []);

  // 注册新用户
  const signup = async (email, password, displayName) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // 更新用户资料
      await updateProfile(user, { displayName });
      
      // 创建用户文档到 Firestore
      await setDoc(doc(db, "users", user.uid), {
        email,
        displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        role: 'user',
        favoriteProducts: [],
        cart: []
      });
      
      // 发送验证邮件
      await sendEmailVerification(user);
      
      return user;
    } catch (err) {
      console.error("注册失败:", err);
      setError(err.message);
      throw err;
    }
  };

  // 登录用户
  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      console.log("尝试登录用户:", email, "记住登录状态:", rememberMe);
      
      // 设置登录持久性，但在iOS Safari上总是使用本地持久化，防止会话丢失问题
      const isSafariIOS = isIOSSafari();
      const persistence = (rememberMe || isSafariIOS) ? browserLocalPersistence : browserSessionPersistence;
      console.log("设置持久性模式:", (rememberMe || isSafariIOS) ? "本地持久" : "会话持久", 
                 isSafariIOS ? "(iOS Safari强制设置)" : "");
      
      try {
        await setPersistence(auth, persistence);
        console.log("成功设置持久性");
      } catch (persistenceError) {
        console.error("设置持久性失败:", persistenceError);
        // 不要因为持久性问题中止登录流程
      }
      
      // 执行登录
      console.log("开始执行登录...");
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Firebase登录成功，用户ID:", user.uid);
      
      // 获取用户数据和管理员状态
      const [adminStatus, userDataResult] = await Promise.all([
        checkAdminStatus(user),
        fetchUserData(user)
      ]);
      
      console.log("登录完成，用户状态:", { isAdmin: adminStatus, userData: userDataResult });
      setIsAdmin(adminStatus);
      setUserData(userDataResult);
      
      return user;
    } catch (err) {
      console.error("登录失败:", err.code, err.message);
      let errorMessage = "登录失败，请检查您的邮箱和密码";
      
      switch(err.code) {
        case 'auth/user-not-found':
          errorMessage = "该邮箱未注册";
          break;
        case 'auth/wrong-password':
          errorMessage = "密码错误";
          break;
        case 'auth/too-many-requests':
          errorMessage = "登录尝试次数过多，请稍后再试";
          break;
        case 'auth/user-disabled':
          errorMessage = "该账号已被禁用，请联系客服";
          break;
        case 'auth/invalid-credential':
          errorMessage = "无效的登录凭证";
          break;
        case 'auth/invalid-email':
          errorMessage = "邮箱格式不正确";
          break;
        case 'auth/network-request-failed':
          errorMessage = "网络连接错误，请检查您的网络";
          break;
        case 'auth/internal-error':
          errorMessage = "Firebase内部错误，请稍后重试";
          break;
        case 'auth/web-storage-unsupported':
          errorMessage = "您的浏览器不支持网页存储，请启用Cookie或使用其他浏览器";
          break;
        default:
          errorMessage = `登录失败: ${err.message}`;
      }
      
      console.log("设置错误信息:", errorMessage);
      setError(errorMessage);
      throw err;
    }
  };

  // 辅助函数：保存Google用户头像到Firebase Storage，避免CSP问题
  const saveUserAvatar = async (user) => {
    if (!user || !user.photoURL) return null;
    
    // 检查是否是第三方提供商的URL (Google, Facebook等)
    const isExternalPhotoURL = user.photoURL.includes('googleusercontent.com') || 
                              user.photoURL.includes('fbcdn.net') ||
                              user.photoURL.includes('twimg.com');
    
    if (isExternalPhotoURL) {
      console.log("检测到第三方提供商头像，准备保存到Firebase Storage...");
      try {
        const storage = getStorage();
        const avatarPath = `userAvatars/${user.uid}`;
        const avatarRef = storageRef(storage, avatarPath);
        
        // 获取外部图片并转换为Blob
        const response = await fetch(user.photoURL);
        const blob = await response.blob();
        
        // 上传到Firebase Storage
        console.log("上传头像到Firebase Storage...");
        await uploadBytes(avatarRef, blob);
        
        // 获取存储的URL
        const downloadURL = await getDownloadURL(avatarRef);
        console.log("头像上传成功，URL:", downloadURL);
        
        // 更新用户资料
        await updateProfile(user, { photoURL: downloadURL });
        console.log("用户资料已更新为新的头像URL");
        
        return downloadURL;
      } catch (error) {
        console.error("保存用户头像失败:", error);
        // 失败时返回原始URL，不阻止登录流程
        return user.photoURL;
      }
    }
    return user.photoURL;
  };
  
  // 检测是否是移动设备
  const isMobileDevice = () => {
    const ua = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  };
  
  // 检测是否是 iOS Safari
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    return ua.includes('iPhone') && ua.includes('Safari') && !ua.includes('Chrome');
  };

  // Google登录
  const loginWithGoogle = async (useRedirect = false) => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      // 请求额外的用户资料
      provider.addScope('profile');
      provider.addScope('email');
      
      let userCredential;
      let user;
      
      // 在移动设备上始终使用弹窗方式，避免重定向问题
      const isMobile = isMobileDevice();
      console.log("检测到设备类型:", isMobile ? "移动设备" : "桌面设备");
      
      // 始终在移动设备上使用弹窗，避免重定向问题
      userCredential = await signInWithPopup(auth, provider);
      user = userCredential.user;
      
      // 检查是否是新用户
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser;
      
      // 保存用户头像到Firebase Storage以解决CSP问题
      const safePhotoURL = await saveUserAvatar(user);
      
      // 检查用户是否已存在于 Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData;
      
      if (isNewUser || !userDoc.exists()) {
        // 首次登录，创建用户文档
        // 检查是否是预设的管理员邮箱
        const isAdmin = user.email === 'admin@destinycrystal.com';
        
        userData = {
          email: user.email,
          displayName: user.displayName,
          photoURL: safePhotoURL,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          role: isAdmin ? 'admin' : 'user',
          favoriteProducts: [],
          cart: []
        };
        
        await setDoc(userDocRef, userData);
        console.log("创建新用户:", { isAdmin, email: user.email });
      } else {
        // 获取现有用户数据
        userData = userDoc.data();
        // 更新登录时间和头像URL
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
          photoURL: safePhotoURL,
          updatedAt: serverTimestamp()
        });
      }
      
      // 设置用户状态
      setUserData(userData);
      setIsAdmin(userData.role === 'admin');
      console.log("Google登录完成，用户状态:", { isAdmin: userData.role === 'admin', userData });
      
      return user;
    } catch (err) {
      console.error("Google登录失败:", err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError("登录窗口被关闭");
      } else if (err.code === 'auth/popup-blocked') {
        setError("登录窗口被浏览器阻止，请允许弹窗或尝试重定向登录");
      } else {
        setError("Google登录失败，请稍后重试");
      }
      throw err;
    }
  };
  
  // Facebook登录
  const loginWithFacebook = async () => {
    try {
      setError(null);
      console.log("开始Facebook登录...");
      const provider = new FacebookAuthProvider();
      
      // 添加额外权限
      provider.addScope('email');
      provider.addScope('public_profile');
      
      // 总是使用弹窗方式
      console.log("使用弹窗方式进行Facebook登录");
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      console.log("Facebook登录成功，用户ID:", user.uid);
      
      // 检查是否是新用户
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser;
      console.log("是否新用户:", isNewUser ? "是" : "否");
      
      // 处理Facebook头像，保存到Firebase Storage以解决CSP问题
      console.log("处理用户头像...");
      const safePhotoURL = await saveUserAvatar(user);
      
      // 用户文档操作
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (isNewUser || !userDoc.exists()) {
        // 创建用户文档
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          photoURL: safePhotoURL, // 使用安全的URL
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          role: 'user',
          favoriteProducts: [],
          cart: []
        });
      } else {
        // 更新头像URL和登录时间
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
          photoURL: safePhotoURL // 更新为安全的URL
        });
      }
      
      return user;
    } catch (err) {
      console.error("Facebook登录失败:", err);
      setError("Facebook登录失败，请稍后重试");
      throw err;
    }
  };

  // 登出
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserData(null);
      setIsAdmin(false);
    } catch (err) {
      console.error("登出失败:", err);
      setError("登出失败，请重试");
      throw err;
    }
  };

  // 重置密码
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error("重置密码失败:", err);
      setError("重置密码失败，请确认邮箱地址正确");
      throw err;
    }
  };

  // 更新用户个人资料
  const updateUserProfile = async (newData) => {
    try {
      setError(null);
      const user = auth.currentUser;
      
      if (!user) throw new Error("用户未登录");
      
      // 更新 Firebase Auth 显示名
      if (newData.displayName) {
        await updateProfile(user, { displayName: newData.displayName });
      }
      
      // 更新头像
      if (newData.photoURL) {
        await updateProfile(user, { photoURL: newData.photoURL });
      }
      
      // 更新 Firestore 数据
      const userDocRef = doc(db, "users", user.uid);
      
      // 移除不需要存储的字段
      const { password, passwordConfirm, ...updateData } = newData;
      
      await updateDoc(userDocRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      // 更新本地状态
      const updatedDoc = await getDoc(userDocRef);
      setUserData(updatedDoc.data());
      
      return true;
    } catch (err) {
      console.error("更新个人资料失败:", err);
      setError("更新个人资料失败，请稍后重试");
      throw err;
    }
  };

  // 更新邮箱
  const updateUserEmail = async (newEmail, password) => {
    try {
      setError(null);
      const user = auth.currentUser;
      
      if (!user) throw new Error("用户未登录");
      
      // 重新认证用户
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      
      // 更新邮箱
      await updateEmail(user, newEmail);
      
      // 更新 Firestore
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        email: newEmail,
        updatedAt: serverTimestamp()
      });
      
      // 更新本地状态
      const updatedDoc = await getDoc(userDocRef);
      setUserData(updatedDoc.data());
      
      return true;
    } catch (err) {
      console.error("更新邮箱失败:", err);
      setError("更新邮箱失败，请确认当前密码正确");
      throw err;
    }
  };

  // 更新密码
  const updateUserPassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const user = auth.currentUser;
      
      if (!user) throw new Error("用户未登录");
      
      // 重新认证用户
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // 更新密码
      await updatePassword(user, newPassword);
      
      return true;
    } catch (err) {
      console.error("更新密码失败:", err);
      setError("更新密码失败，请确认当前密码正确");
      throw err;
    }
  };

  // 由于我们不再使用重定向登录，这个钩子简化为检查会话恢复
  useEffect(() => {
    console.log("检查是否有保存的登录会话...");
    // 继续监听Auth状态变化，由onAuthStateChanged处理
  }, []);

  // 手机号登录 - 初始化reCAPTCHA
  const initPhoneAuth = (containerId) => {
    try {
      console.log("初始化手机号验证的reCAPTCHA...");
      const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'normal',
        callback: (response) => {
          console.log("reCAPTCHA已验证成功");
        },
        'expired-callback': () => {
          console.log("reCAPTCHA已过期，请重新验证");
          setError("验证码已过期，请重新验证");
        }
      });
      return recaptchaVerifier;
    } catch (error) {
      console.error("初始化reCAPTCHA失败:", error);
      throw error;
    }
  };

  // 发送手机验证码
  const sendPhoneVerificationCode = async (phoneNumber, recaptchaVerifier) => {
    try {
      console.log("发送验证码到手机:", phoneNumber);
      setError(null);
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      console.log("验证码已发送");
      return confirmationResult;
    } catch (error) {
      console.error("发送验证码失败:", error);
      let errorMessage;
      
      switch(error.code) {
        case 'auth/invalid-phone-number':
          errorMessage = "无效的手机号码格式";
          break;
        case 'auth/missing-phone-number':
          errorMessage = "请提供手机号码";
          break;
        case 'auth/quota-exceeded':
          errorMessage = "短信验证码发送次数已超限";
          break;
        case 'auth/user-disabled':
          errorMessage = "该手机号已被禁用";
          break;
        case 'auth/captcha-check-failed':
          errorMessage = "人机验证失败";
          break;
        default:
          errorMessage = "发送验证码失败，请稍后重试";
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // 手机验证码登录
  const loginWithPhone = async (confirmationResult, verificationCode) => {
    try {
      console.log("验证手机验证码...");
      setError(null);
      const userCredential = await confirmationResult.confirm(verificationCode);
      const user = userCredential.user;
      console.log("手机验证成功，用户ID:", user.uid);
      
      // 检查用户是否存在于Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData;
      if (!userDoc.exists()) {
        // 创建新用户
        userData = {
          phoneNumber: user.phoneNumber,
          displayName: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          role: 'user',
          favoriteProducts: [],
          cart: []
        };
        
        await setDoc(userDocRef, userData);
        console.log("创建新用户 (手机号):", user.phoneNumber);
      } else {
        // 更新登录时间
        userData = userDoc.data();
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      setUserData(userData);
      setIsAdmin(userData.role === 'admin');
      
      return user;
    } catch (error) {
      console.error("手机验证登录失败:", error);
      let errorMessage;
      
      switch(error.code) {
        case 'auth/invalid-verification-code':
          errorMessage = "验证码无效";
          break;
        case 'auth/code-expired':
          errorMessage = "验证码已过期";
          break;
        default:
          errorMessage = "验证失败，请重试";
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // Apple登录
  const loginWithApple = async () => {
    try {
      console.log("开始Apple登录...");
      setError(null);
      
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      // 始终使用弹窗方式
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      console.log("Apple登录成功，用户ID:", user.uid);
      
      // 检查是否是新用户
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser;
      
      // 用户文档操作
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData;
      if (isNewUser || !userDoc.exists()) {
        // 创建用户文档
        userData = {
          email: user.email,
          displayName: user.displayName || "",
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          role: 'user',
          favoriteProducts: [],
          cart: []
        };
        
        await setDoc(userDocRef, userData);
      } else {
        // 获取现有用户数据并更新登录时间
        userData = userDoc.data();
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // 更新状态
      setUserData(userData);
      setIsAdmin(userData.role === 'admin');
      
      return user;
    } catch (error) {
      console.error("Apple登录失败:", error);
      setError("Apple登录失败，请稍后重试");
      throw error;
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    error,
    isAdmin,
    signup,
    login,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    loginWithPhone,
    initPhoneAuth,
    sendPhoneVerificationCode,
    logout,
    resetPassword,
    updateUserProfile,
    updateUserEmail,
    updateUserPassword,
    isVerified: currentUser?.emailVerified === true,
    clearError,
    setUserData,
    setIsAdmin,
    googleLogin: async () => {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });
      // 始终使用弹窗登录方式，而不是重定向
      return signInWithPopup(auth, provider);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
